import { useWebBooking } from "api/api-hooks/use-web-booking";
import ConfirmationDialog from "components/confirm-dialog";
import { useBookingGuard } from "hooks/use-booking-guard";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { checkIsExperienceViewPage, checkIsMyBookingPage } from "routes";
import { getSearchParams } from "search-params";
import { useCancelBooking } from "api/api-hooks/use-cancel-booking";

export function BookingGuard({ onOpenFilter }: { onOpenFilter?: () => void }) {
    const { t } = useTranslation();
    const { blocker } = useBookingGuard();
    const bookingId = useBookingId();
    const { isActivityBooking, isWaitList } = useBookingStateContext();
    const { data: booking } = useWebBooking();
    const { system } = useParams();
    const navigate = useNavigate();

    const isMyBookingPage = checkIsMyBookingPage(location.pathname);
    const isExperienceViewPage = checkIsExperienceViewPage(location.pathname);
    const bookingMode = isMyBookingPage
        ? "myBooking"
        : isExperienceViewPage
          ? "experienceView"
          : "activityBooking";

    const title = t(`bookingGuard.${bookingMode}.title`);
    const message = t(`bookingGuard.${bookingMode}.message`);

    const cancelBooking = useCancelBooking();
    const isBookingFinalized = booking?.isCompleted();
    useEffect(() => {
        if (
            blocker.state === "blocked" &&
            (!isActivityBooking || isWaitList) &&
            booking &&
            !isBookingFinalized &&
            !cancelBooking.isPending
        ) {
            cancelBooking.mutate({ systemTriggered: true });
            blocker.proceed?.();
        }
    }, [isActivityBooking, isWaitList, booking, blocker, cancelBooking, isBookingFinalized]);

    const startOver = () => {
        const { isExperienceViewMode } = getSearchParams();
        let bookMoreUrl = `/${system}/${booking?.unitId}`;
        if (isActivityBooking) bookMoreUrl += "/activity-booking";
        if (isExperienceViewMode) bookMoreUrl += "/experience-view";
        void navigate(
            { pathname: bookMoreUrl, search: window.location.search },
            { state: { intercept: false } }
        );
    };

    const onConfirmLeave = () => {
        // A double tap lands here again once the blocker has moved on, and proceed() then throws.
        if (blocker.state !== "blocked") return;

        if (isMyBookingPage) {
            startOver();
            return;
        }

        if (bookingId && booking && !isBookingFinalized) {
            cancelBooking.mutate({});
        }

        if (isExperienceViewPage && onOpenFilter) {
            onOpenFilter();
        }

        blocker.proceed?.();
    };

    const onCancel = () => {
        blocker.reset?.();
    };

    return (
        <>
            {((isActivityBooking && !isWaitList) || isMyBookingPage) && (
                <ConfirmationDialog
                    isOpen={blocker?.state === "blocked"}
                    title={title}
                    message={message}
                    primaryAction={onConfirmLeave}
                    secondaryAction={onCancel}
                    onClose={onCancel}
                    primaryButtonText={t(`bookingGuard.${bookingMode}.confirm`)}
                    secondaryButtonText={t(`bookingGuard.${bookingMode}.abort`)}
                />
            )}
        </>
    );
}
